import { Controller, useFormContext, useWatch } from 'react-hook-form';
import FormTextInput from 'components/Find/FormTextInput';
import TabSwitcher from 'components/TabSwitcher';
import Select from 'components/forms/Select';
import {
  SELF_RATE_RATING_SYSTEM,
  SKILL_LEVEL_MODE_OPTIONS,
  SkillLevelMode,
  type SkillLevelRangeFieldProps,
  type SkillLevelRangeFormValues,
} from './types';

const parseRating = (raw: string): number | null => {
  if (!raw.trim()) return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

function SubLabel({ children }: { children: string }) {
  return (
    <span className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
      {children}
    </span>
  );
}

/**
 * Figma "Skill level": a Specified / All levels switch, then ONE rating system for
 * the sport (its scales + "Self rate") and a min–max range. A scale's range is
 * numeric; self-rate's range is a pair of skill levels. Reads its fields from the
 * enclosing `FormProvider` (the game and availability forms both include them).
 */
export default function SkillLevelRangeField({ scales, skillLevels }: SkillLevelRangeFieldProps) {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<SkillLevelRangeFormValues>();
  const mode = useWatch({ control, name: 'skillLevelMode' });
  const ratingSystem = useWatch({ control, name: 'ratingSystem' });
  const minLevel = useWatch({ control, name: 'selfRatedSkillLevel' });
  const maxLevel = useWatch({ control, name: 'selfRatedMaxSkillLevel' });

  const scale = scales.find((option) => option.id === ratingSystem);
  const isSelfRate = ratingSystem === SELF_RATE_RATING_SYSTEM;

  const systemOptions = [
    ...scales.map((option) => ({ value: option.id, label: option.shortName })),
    { value: SELF_RATE_RATING_SYSTEM, label: 'Self rate' },
  ];
  const levelOptions = skillLevels.map((level) => ({ value: level.id, label: level.displayName }));
  const rankOf = (levelId: string | null) => levelOptions.findIndex((o) => o.value === levelId);

  // Switching systems resets the range so a DUPR range never leaks into UTR.
  const handleSystemChange = () => {
    setValue('ratingMinimum', null);
    setValue('ratingMaximum', null);
    setValue('selfRatedSkillLevel', null);
    setValue('selfRatedMaxSkillLevel', null);
  };

  // Keep the level range ordered by nudging the other bound rather than erroring.
  const handleMinLevelChange = (levelId: string) => {
    if (maxLevel == null || rankOf(maxLevel) < rankOf(levelId)) {
      setValue('selfRatedMaxSkillLevel', levelId);
    }
  };
  const handleMaxLevelChange = (levelId: string) => {
    if (minLevel == null || rankOf(minLevel) > rankOf(levelId)) {
      setValue('selfRatedSkillLevel', levelId);
    }
  };

  return (
    <div className="flex flex-col gap-ds-lg">
      <Controller
        control={control}
        name="skillLevelMode"
        render={({ field }) => (
          <TabSwitcher
            options={SKILL_LEVEL_MODE_OPTIONS}
            selectedValue={field.value}
            onValueChange={field.onChange}
            activeFillInvert
            wrapperClassName="!bg-color-bg-lightmode-secondary dark:!bg-color-bg-darkmode-secondary"
          />
        )}
      />

      {mode === SkillLevelMode.Specified && (
        <>
          <div className="flex flex-col gap-ds-sm">
            <SubLabel>Rating system</SubLabel>
            <Select
              control={control}
              name="ratingSystem"
              errors={errors}
              placeholder="Choose a rating system"
              options={systemOptions}
              onChange={handleSystemChange}
            />
          </div>

          {(scale || isSelfRate) && (
            <div className="flex flex-col gap-ds-sm">
              <SubLabel>Range</SubLabel>
              <div className="flex items-center gap-ds-md">
                {isSelfRate ? (
                  <>
                    <div className="min-w-0 flex-1">
                      <Select
                        control={control}
                        name="selfRatedSkillLevel"
                        errors={errors}
                        placeholder="From"
                        options={levelOptions}
                        onChange={handleMinLevelChange}
                      />
                    </div>
                    <span className="typography-product-body-highlight text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                      -
                    </span>
                    <div className="min-w-0 flex-1">
                      <Select
                        control={control}
                        name="selfRatedMaxSkillLevel"
                        errors={errors}
                        placeholder="To"
                        options={levelOptions}
                        onChange={handleMaxLevelChange}
                      />
                    </div>
                  </>
                ) : (
                  scale && (
                    <>
                      <Controller
                        control={control}
                        name="ratingMinimum"
                        render={({ field }) => (
                          <div className="min-w-0 flex-1">
                            <FormTextInput
                              type="number"
                              inputMode="decimal"
                              step="0.5"
                              placeholder={String(scale.minimum)}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(parseRating(e.target.value))}
                              className="text-center"
                            />
                          </div>
                        )}
                      />
                      <span className="typography-product-body-highlight text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                        -
                      </span>
                      <Controller
                        control={control}
                        name="ratingMaximum"
                        render={({ field, fieldState }) => (
                          <div className="min-w-0 flex-1">
                            <FormTextInput
                              type="number"
                              inputMode="decimal"
                              step="0.5"
                              placeholder={String(scale.maximum)}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(parseRating(e.target.value))}
                              className="text-center"
                            />
                            {fieldState.error && (
                              <p className="typography-product-caption mt-ds-xs text-color-text-lightmode-error dark:text-color-text-darkmode-error">
                                {fieldState.error.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    </>
                  )
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
